import React, { useEffect, useRef, useState } from 'react';
import './SemanticExplorer.css';

const SemanticExplorer = ({ searchQuery, onBackToStandard }) => {
    const [selectedNode, setSelectedNode] = useState(null);
    const [graphData, setGraphData] = useState(null);
    const [zoom, setZoom] = useState(1);
    const svgRef = useRef(null);

    // Mock data for the semantic graph
    const generateGraphData = (query) => {
        // Define different medical concept sets based on query keywords
        const conceptSets = {
            'diabetes': {
                conditions: ['Type 1 Diabetes', 'Type 2 Diabetes', 'Gestational Diabetes', 'Insulin Resistance', 'Metabolic Syndrome'],
                treatments: ['Metformin', 'Insulin Therapy', 'Sulfonylureas', 'DPP-4 Inhibitors', 'GLP-1 Agonists'],
                symptoms: ['Excessive Thirst', 'Frequent Urination', 'Fatigue', 'Blurred Vision', 'Slow Healing'],
                research: ['Prediabetes', 'Diabetes Prevention', 'Beta Cell Function', 'Glycemic Control', 'Complications']
            },
            'cancer': {
                conditions: ['Breast Cancer', 'Lung Cancer', 'Colon Cancer', 'Prostate Cancer', 'Leukemia'],
                treatments: ['Chemotherapy', 'Radiation Therapy', 'Immunotherapy', 'Targeted Therapy', 'Surgery'],
                symptoms: ['Fatigue', 'Weight Loss', 'Pain', 'Fever', 'Night Sweats'],
                research: ['Early Detection', 'Precision Medicine', 'Biomarkers', 'Clinical Trials', 'Survivorship']
            },
            'heart': {
                conditions: ['Coronary Artery Disease', 'Heart Failure', 'Arrhythmia', 'Hypertension', 'Cardiomyopathy'],
                treatments: ['ACE Inhibitors', 'Beta Blockers', 'Statins', 'Cardiac Surgery', 'Pacemakers'],
                symptoms: ['Chest Pain', 'Shortness of Breath', 'Fatigue', 'Swelling', 'Palpitations'],
                research: ['Cardiovascular Prevention', 'Heart Regeneration', 'Risk Factors', 'Biomarkers', 'Lifestyle']
            },
            'asthma': {
                conditions: ['Allergic Asthma', 'Non-Allergic Asthma', 'Exercise-Induced Asthma', 'Severe Asthma', 'COPD'],
                treatments: ['Inhaled Corticosteroids', 'Bronchodilators', 'Leukotriene Modifiers', 'Biologics', 'Allergy Shots'],
                symptoms: ['Wheezing', 'Coughing', 'Shortness of Breath', 'Chest Tightness', 'Rapid Breathing'],
                research: ['Asthma Control', 'Trigger Identification', 'Biomarkers', 'Precision Medicine', 'Prevention']
            },
            'mental': {
                conditions: ['Depression', 'Anxiety', 'Bipolar Disorder', 'Schizophrenia', 'PTSD'],
                treatments: ['Antidepressants', 'Antipsychotics', 'Psychotherapy', 'CBT', 'ECT'],
                symptoms: ['Mood Changes', 'Sleep Problems', 'Concentration Issues', 'Social Withdrawal', 'Appetite Changes'],
                research: ['Neurobiology', 'Early Intervention', 'Precision Psychiatry', 'Digital Therapeutics', 'Stigma']
            }
        };

        // Determine which concept set to use based on query
        let selectedSet = 'diabetes'; // default
        const queryLower = query.toLowerCase();

        if (queryLower.includes('cancer') || queryLower.includes('tumor') || queryLower.includes('oncology') || queryLower.includes('immunotherapy')) {
            selectedSet = 'cancer';
        } else if (queryLower.includes('heart') || queryLower.includes('cardiac') || queryLower.includes('cardiovascular')) {
            selectedSet = 'heart';
        } else if (queryLower.includes('asthma') || queryLower.includes('respiratory') || queryLower.includes('breathing')) {
            selectedSet = 'asthma';
        } else if (queryLower.includes('mental') || queryLower.includes('depression') || queryLower.includes('anxiety') || queryLower.includes('psychiatric')) {
            selectedSet = 'mental';
        } else if (queryLower.includes('diabetes') || queryLower.includes('blood sugar') || queryLower.includes('glucose') || queryLower.includes('high blood sugar')) {
            selectedSet = 'diabetes';
        }

        const concepts = conceptSets[selectedSet];

        // Generate nodes with dynamic positioning and better spacing
        const nodes = [
            // Central node (search query)
            {
                id: query,
                label: query,
                type: 'query',
                size: 80,
                color: '#4A90E2',
                x: 400,
                y: 300
            }
        ];

        // Add condition nodes with better spacing
        concepts.conditions.forEach((condition, index) => {
            const angle = (index / concepts.conditions.length) * 2 * Math.PI;
            const radius = 250;
            nodes.push({
                id: condition.toLowerCase().replace(/\s+/g, '-'),
                label: condition,
                type: 'condition',
                size: 60 + Math.random() * 20,
                color: '#4A90E2',
                x: 400 + radius * Math.cos(angle),
                y: 300 + radius * Math.sin(angle)
            });
        });

        // Add treatment nodes with better spacing
        concepts.treatments.forEach((treatment, index) => {
            const angle = (index / concepts.treatments.length) * 2 * Math.PI;
            const radius = 350;
            nodes.push({
                id: treatment.toLowerCase().replace(/\s+/g, '-'),
                label: treatment,
                type: 'treatment',
                size: 50 + Math.random() * 15,
                color: '#FF6B35',
                x: 400 + radius * Math.cos(angle),
                y: 300 + radius * Math.sin(angle)
            });
        });

        // Add symptom nodes with better spacing
        concepts.symptoms.forEach((symptom, index) => {
            const angle = (index / concepts.symptoms.length) * 2 * Math.PI;
            const radius = 180;
            nodes.push({
                id: symptom.toLowerCase().replace(/\s+/g, '-'),
                label: symptom,
                type: 'symptom',
                size: 40 + Math.random() * 10,
                color: '#9B59B6',
                x: 400 + radius * Math.cos(angle),
                y: 300 + radius * Math.sin(angle)
            });
        });

        // Add research nodes with better spacing
        concepts.research.forEach((research, index) => {
            const angle = (index / concepts.research.length) * 2 * Math.PI;
            const radius = 420;
            nodes.push({
                id: research.toLowerCase().replace(/\s+/g, '-'),
                label: research,
                type: 'research',
                size: 55 + Math.random() * 15,
                color: '#8E44AD',
                x: 400 + radius * Math.cos(angle),
                y: 300 + radius * Math.sin(angle)
            });
        });

        // Generate links dynamically
        const links = [];

        // Connect central query to all condition nodes
        concepts.conditions.forEach(condition => {
            const conditionId = condition.toLowerCase().replace(/\s+/g, '-');
            links.push({
                source: query,
                target: conditionId,
                strength: 0.9 + Math.random() * 0.1,
                color: '#00D4AA'
            });
        });

        // Connect conditions to treatments (reduced connections for less congestion)
        concepts.conditions.forEach(condition => {
            const conditionId = condition.toLowerCase().replace(/\s+/g, '-');
            concepts.treatments.forEach(treatment => {
                const treatmentId = treatment.toLowerCase().replace(/\s+/g, '-');
                if (Math.random() > 0.6) { // 40% chance of connection
                    links.push({
                        source: conditionId,
                        target: treatmentId,
                        strength: 0.7 + Math.random() * 0.3
                    });
                }
            });
        });

        // Connect conditions to symptoms (reduced connections)
        concepts.conditions.forEach(condition => {
            const conditionId = condition.toLowerCase().replace(/\s+/g, '-');
            concepts.symptoms.forEach(symptom => {
                const symptomId = symptom.toLowerCase().replace(/\s+/g, '-');
                if (Math.random() > 0.7) { // 30% chance of connection
                    links.push({
                        source: conditionId,
                        target: symptomId,
                        strength: 0.5 + Math.random() * 0.4
                    });
                }
            });
        });

        // Connect to research areas (reduced connections)
        concepts.conditions.forEach(condition => {
            const conditionId = condition.toLowerCase().replace(/\s+/g, '-');
            concepts.research.forEach(research => {
                const researchId = research.toLowerCase().replace(/\s+/g, '-');
                if (Math.random() > 0.75) { // 25% chance of connection
                    links.push({
                        source: conditionId,
                        target: researchId,
                        strength: 0.6 + Math.random() * 0.3
                    });
                }
            });
        });

        return { nodes, links };
    };

    useEffect(() => {
        if (searchQuery) {
            setGraphData(generateGraphData(searchQuery));
        }
    }, [searchQuery]);

    const handleNodeClick = (node) => {
        // Prevent flickering by checking if node is already selected
        if (selectedNode?.id !== node.id) {
            setSelectedNode(node);
        }
    };

    const handleZoomIn = () => {
        setZoom(prev => Math.min(prev * 1.2, 3));
    };

    const handleZoomOut = () => {
        setZoom(prev => Math.max(prev / 1.2, 0.3));
    };

    const handleReset = () => {
        setZoom(1);
        setSelectedNode(null);
    };

    const handleBackToStandardView = () => {
        console.log('Back to standard view clicked');
        if (onBackToStandard && typeof onBackToStandard === 'function') {
            onBackToStandard();
        } else {
            console.error('onBackToStandard is not a function:', onBackToStandard);
        }
    };

    const getNodeInfo = (node) => {
        // Generate dynamic information based on node type and label
        const generateDefinition = (label, type) => {
            const definitions = {
                condition: [
                    `A condition characterized by ${label.toLowerCase()}.`,
                    `${label} is a health condition that affects various aspects of patient care.`,
                    `A clinical condition related to ${label.toLowerCase()} that requires medical attention.`
                ],
                treatment: [
                    `A therapeutic intervention used to treat ${label.toLowerCase()}.`,
                    `${label} is a medical treatment option for various conditions.`,
                    `A clinical therapy approach involving ${label.toLowerCase()}.`
                ],
                symptom: [
                    `A common symptom associated with ${label.toLowerCase()}.`,
                    `${label} is a clinical manifestation of various medical conditions.`,
                    `A patient-reported symptom related to ${label.toLowerCase()}.`
                ],
                research: [
                    `An area of research focused on ${label.toLowerCase()}.`,
                    `${label} represents a field of medical research and investigation.`,
                    `A research domain exploring ${label.toLowerCase()} in clinical settings.`
                ]
            };

            const typeDefs = definitions[type] || definitions.condition;
            return typeDefs[Math.floor(Math.random() * typeDefs.length)];
        };

        const generateStats = (type) => {
            const baseStats = {
                condition: { minArticles: 80, maxArticles: 600, minRelevance: 75 },
                treatment: { minArticles: 50, maxArticles: 300, minRelevance: 80 },
                symptom: { minArticles: 30, maxArticles: 150, minRelevance: 65 },
                research: { minArticles: 60, maxArticles: 400, minRelevance: 70 }
            };

            const stats = baseStats[type] || baseStats.condition;
            const articles = Math.floor(Math.random() * (stats.maxArticles - stats.minArticles)) + stats.minArticles;
            const relevance = Math.floor(Math.random() * 20) + stats.minRelevance;

            const strengths = ['Low', 'Medium', 'High'];
            const strength = strengths[Math.floor(Math.random() * strengths.length)];

            return { articles, relevance, strength };
        };

        const stats = generateStats(node.type);

        return {
            definition: generateDefinition(node.label, node.type),
            articles: stats.articles,
            strength: stats.strength,
            relevance: stats.relevance
        };
    };

    if (!graphData) return <div className="loading">Loading semantic graph...</div>;

    return (
        <div className="semantic-explorer">
            {/* Header */}
            <div className="explorer-header">
                <div className="header-left">
                    <div className="breadcrumbs">
                        <span>Home</span>
                        <i className="fas fa-chevron-right"></i>
                        <span>Search</span>
                        <i className="fas fa-chevron-right"></i>
                        <span>Semantic Explorer</span>
                    </div>
                    <h1>Semantic Explorer</h1>
                </div>

                <div className="search-section">
                    <div className="search-bar">
                        <input
                            type="text"
                            value={searchQuery}
                            readOnly
                            placeholder="Search medical literature..."
                        />
                        <div className="search-tag">{searchQuery}</div>
                    </div>
                </div>

                <div className="view-controls">
                    <div className="view-toggle">
                        <span className="toggle-label">Standard View</span>
                        <div className="toggle-switch active">
                            <div className="toggle-slider"></div>
                        </div>
                        <span className="toggle-label active">Explorer View</span>
                    </div>

                    <div className="graph-controls">
                        <button onClick={handleZoomIn} title="Zoom In">
                            <i className="fas fa-search-plus"></i>
                        </button>
                        <button onClick={handleZoomOut} title="Zoom Out">
                            <i className="fas fa-search-minus"></i>
                        </button>
                        <button onClick={handleReset} title="Reset View">
                            <i className="fas fa-redo"></i>
                        </button>
                        <button title="Fullscreen">
                            <i className="fas fa-expand"></i>
                        </button>
                        <button onClick={handleBackToStandardView} title="Close">
                            <i className="fas fa-times"></i>
                        </button>
                    </div>

                    <div className="legend">
                        <div className="legend-item">
                            <div className="legend-color condition"></div>
                            <span>Conditions</span>
                        </div>
                        <div className="legend-item">
                            <div className="legend-color treatment"></div>
                            <span>Treatments</span>
                        </div>
                        <div className="legend-item">
                            <div className="legend-color symptom"></div>
                            <span>Symptoms</span>
                        </div>
                        <div className="legend-item">
                            <div className="legend-color research"></div>
                            <span>Research</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Graph Visualization */}
            <div className="graph-container">
                <svg
                    ref={svgRef}
                    width="100%"
                    height="100%"
                    viewBox="0 0 800 600"
                    style={{ transform: `scale(${zoom})` }}
                >
                    <defs>
                        <filter id="glow">
                            <feGaussianBlur stdDeviation="4" result="coloredBlur" />
                            <feMerge>
                                <feMergeNode in="coloredBlur" />
                                <feMergeNode in="SourceGraphic" />
                            </feMerge>
                        </filter>
                        <filter id="nodeGlow">
                            <feGaussianBlur stdDeviation="2" result="coloredBlur" />
                            <feMerge>
                                <feMergeNode in="coloredBlur" />
                                <feMergeNode in="SourceGraphic" />
                            </feMerge>
                        </filter>
                        <filter id="textGlow">
                            <feGaussianBlur stdDeviation="1" result="coloredBlur" />
                            <feMerge>
                                <feMergeNode in="coloredBlur" />
                                <feMergeNode in="SourceGraphic" />
                            </feMerge>
                        </filter>
                        <filter id="textGlow">
                            <feGaussianBlur stdDeviation="1" result="coloredBlur" />
                            <feMerge>
                                <feMergeNode in="coloredBlur" />
                                <feMergeNode in="SourceGraphic" />
                            </feMerge>
                        </filter>
                    </defs>

                    {/* Background grid */}
                    <pattern id="grid" width="25" height="25" patternUnits="userSpaceOnUse">
                        <path d="M 25 0 L 0 0 0 25" fill="none" stroke="#1a1a2e" strokeWidth="0.5" opacity="0.2" />
                    </pattern>
                    <rect width="100%" height="100%" fill="url(#grid)" />

                    {/* Scattered dots */}
                    {Array.from({ length: 50 }, (_, i) => (
                        <circle
                            key={i}
                            cx={Math.random() * 800}
                            cy={Math.random() * 600}
                            r={Math.random() * 1.5 + 0.3}
                            fill="white"
                            opacity="0.2"
                        />
                    ))}

                    {/* Links */}
                    {graphData.links.map((link, index) => (
                        <g key={index}>
                            <line
                                x1={graphData.nodes.find(n => n.id === link.source)?.x || 0}
                                y1={graphData.nodes.find(n => n.id === link.source)?.y || 0}
                                x2={graphData.nodes.find(n => n.id === link.target)?.x || 0}
                                y2={graphData.nodes.find(n => n.id === link.target)?.y || 0}
                                stroke={link.color || "#ffffff"}
                                strokeWidth={link.strength * 2}
                                opacity="0.5"
                                filter="url(#nodeGlow)"
                            />
                            {/* Animated dots on links */}
                            <circle
                                cx={graphData.nodes.find(n => n.id === link.source)?.x || 0}
                                cy={graphData.nodes.find(n => n.id === link.source)?.y || 0}
                                r="2"
                                fill={link.color || "#ffffff"}
                                opacity="0.7"
                                className="link-dot"
                            />
                        </g>
                    ))}

                    {/* Nodes */}
                    {graphData.nodes.map((node) => (
                        <g
                            key={node.id}
                            onClick={() => handleNodeClick(node)}
                            style={{ cursor: 'pointer' }}
                            className={selectedNode?.id === node.id ? 'selected' : ''}
                        >
                            <circle
                                cx={node.x}
                                cy={node.y}
                                r={node.size / 2}
                                fill={node.color}
                                stroke={selectedNode?.id === node.id ? "#00D4AA" : "#ffffff"}
                                strokeWidth={selectedNode?.id === node.id ? 4 : 2}
                                opacity="0.95"
                                filter={selectedNode?.id === node.id ? "url(#glow)" : "url(#nodeGlow)"}
                            />

                            {/* Text background for better readability */}
                            <rect
                                x={node.x - Math.max(50, node.label.length * 3.5)}
                                y={node.y + 8}
                                width={Math.max(100, node.label.length * 7)}
                                height="18"
                                fill="rgba(0, 0, 0, 0.85)"
                                rx="9"
                                opacity="0.9"
                            />

                            <text
                                x={node.x}
                                y={node.y + 20}
                                textAnchor="middle"
                                fill="white"
                                fontSize="10"
                                fontWeight="600"
                                pointerEvents="none"
                                filter="url(#textGlow)"
                                style={{
                                    textShadow: '0px 1px 2px rgba(0,0,0,0.8)',
                                    fontFamily: 'Arial, sans-serif',
                                    letterSpacing: '0.5px'
                                }}
                            >
                                {node.label.length > 16 ? node.label.substring(0, 16) + '...' : node.label}
                            </text>
                        </g>
                    ))}
                </svg>
            </div>

            {/* Information Panel */}
            {selectedNode && (
                <div className="info-panel">
                    <div className="info-content">
                        <div className="info-header">
                            <i className="fas fa-cog"></i>
                            <h3>{selectedNode.label}</h3>
                        </div>
                        <p className="info-definition">
                            {getNodeInfo(selectedNode).definition}
                        </p>
                        <div className="info-stats">
                            <div className="stat">
                                <span className="stat-label">Related articles:</span>
                                <span className="stat-value">{getNodeInfo(selectedNode).articles}</span>
                            </div>
                            <div className="stat">
                                <span className="stat-label">Connection strength:</span>
                                <span className="stat-value">{getNodeInfo(selectedNode).strength}</span>
                            </div>
                            <div className="stat">
                                <span className="stat-label">Relevance score:</span>
                                <span className="stat-value">{getNodeInfo(selectedNode).relevance}%</span>
                            </div>
                        </div>
                    </div>
                    <div className="info-actions">
                        <button className="action-btn primary">
                            <i className="fas fa-search"></i>
                            Q Search this concept
                        </button>
                        <button className="action-btn">
                            <i className="fas fa-plus"></i>
                            + Add to search
                        </button>
                        <button className="action-btn" onClick={handleBackToStandardView}>
                            <i className="fas fa-arrow-left"></i>
                            ← Return to standard view
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SemanticExplorer;
